import React, { createContext, useCallback, useContext, useState } from "react";
import * as SharedMessageTypes from "../../darvin-shared/schema/zod/agent/message-types.schema";
import { ImagePreviewModal } from "../ui/image_preview/ImagePreviewModal";

interface ImagePreviewContextType {
  openPreview: (img: SharedMessageTypes.UserMessageAttachment) => void;
  closePreview: () => void;
}

const ImagePreviewContext = createContext<ImagePreviewContextType | undefined>(
  undefined
);

export const useImagePreview = () => {
  const context = useContext(ImagePreviewContext);
  if (!context) {
    throw new Error(
      "useImagePreview must be used within an ImagePreviewProvider"
    );
  }
  return context;
};

export const ImagePreviewProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentImage, setCurrentImage] =
    useState<SharedMessageTypes.UserMessageAttachment | null>(null);

  const openPreview = useCallback(
    (img: SharedMessageTypes.UserMessageAttachment) => {
      setCurrentImage(img);
      setIsOpen(true);
    },
    []
  );

  const closePreview = useCallback(() => {
    setIsOpen(false);
    // Optional: Clear the image after a short delay to prevent flickering
    setTimeout(() => setCurrentImage(null), 200);
  }, []);

  return (
    <ImagePreviewContext.Provider value={{ openPreview, closePreview }}>
      {children}
      {currentImage && (
        <ImagePreviewModal
          img={currentImage}
          isOpen={isOpen}
          onClose={closePreview}
        />
      )}
    </ImagePreviewContext.Provider>
  );
};
